import type { PieceData } from '../types';
import { derivedChorale } from './index';
import { Key } from '../../music/key';
import { HarmonyEvent } from '../../music/piece';
import { Chord } from '../../music/roman';
import { buildView, PieceView } from '../../ui/model';

/**
 * "Herzlich tut mich verlangen" (the Passion Chorale), harmonized by Bach again and again.
 * All settings here share the same melody rhythm (48 quarters, pickup of one beat, the same
 * fermatas), so they align beat by beat once transposed to a common pitch.
 */

export const PASSION_IDS = ['bwv244-15', 'bwv244-17', 'bwv244-44', 'bwv244-54', 'bwv244-62'];
export const ELSEWHERE_IDS = ['bwv153-5', 'bwv161-6', 'bwv270', 'bwv271', 'bwv248-5'];

/** Common pitch for the melody's first note (E4), so every setting is heard against the same tune. */
const REFERENCE_MIDI = 64;

export interface Setting {
  id: string;
  piece: PieceData;
  view: PieceView;
  incipit: string;
  semis: number;
  melody: Array<{ onset: number; dur: number; midi: number; lyric?: string; fermata?: boolean }>;
}

export interface Cell {
  t: number;
  event: HarmonyEvent;
  chord: Chord;
  key: Key;
  tension: number;
  melodyMidi: number;
  lyric?: string;
}

function build(id: string): Setting | null {
  const d = derivedChorale(id);
  if (!d) return null;
  const piece = d.piece;
  const melody = piece.notes
    .filter((n) => n.line === 3)
    .sort((a, b) => a.onset - b.onset)
    .map((n) => ({ onset: n.onset, dur: n.dur, midi: n.midi, lyric: n.lyric, fermata: n.fermata }));
  const semis = REFERENCE_MIDI - melody[0].midi;
  const incipit = piece.title;
  return { id, piece, view: buildView(piece), incipit, semis, melody };
}

let cache: { passion: Setting[]; elsewhere: Setting[] } | null = null;

export function passionSettings(): { passion: Setting[]; elsewhere: Setting[] } {
  if (!cache) {
    cache = {
      passion: PASSION_IDS.map(build).filter(Boolean) as Setting[],
      elsewhere: ELSEWHERE_IDS.map(build).filter(Boolean) as Setting[],
    };
  }
  return cache;
}

/** The chord, key, tension and melody note on every beat of a setting. */
export function cells(s: Setting, beats: number): Cell[] {
  const out: Cell[] = [];
  const events = s.view.events;
  for (let t = 0; t < beats; t++) {
    let i = 0;
    for (let k = events.length - 1; k >= 0; k--) if (events[k].start <= t + 1e-6) {
      i = k;
      break;
    }
    const ev = events[i];
    const mel = s.melody.find((m) => m.onset <= t + 1e-6 && m.onset + m.dur > t + 1e-6) ?? s.melody[0];
    const syl = s.melody.find((m) => Math.abs(m.onset - t) < 1e-6)?.lyric;
    out.push({ t, event: ev, chord: ev.chord, key: ev.key, tension: s.view.tension.perEvent[i].value, melodyMidi: mel.midi + s.semis, lyric: syl });
  }
  return out;
}

export const BEATS = 48;
export const FERMATAS = [7, 13, 23, 29, 39, 45];
